import { useEffect, useMemo, useState } from "react";
import { Store, MapPin, Navigation } from "lucide-react";
import { api } from "../lib/api";

/** Nearby Shops Network — network-style graph of partner shops on the home page.
 *  Desktop: radial layout around a central "You" hub with connecting lines +
 *  distance labels. Mobile: vertical timeline with a connector spine.
 *  Distances auto-compute from user GPS when the shop has coordinates. */
export const ShopNetwork = () => {
  const [shops, setShops] = useState(null);
  const [gpsUsed, setGpsUsed] = useState(false);

  useEffect(() => {
    const fetchShops = (coords) => {
      const params = coords ? { lat: coords.latitude, lng: coords.longitude } : {};
      api.get("/shop-network", { params })
        .then((r) => { setShops(r.data.shops); setGpsUsed(r.data.gps_used); })
        .catch(() => setShops([]));
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchShops(pos.coords),
        () => fetchShops(null),
        { timeout: 5000, maximumAge: 300000 },
      );
    } else fetchShops(null);
  }, []);

  const positioned = useMemo(() => {
    if (!shops) return [];
    return shops.slice(0, 8).map((s, i, arr) => {
      if (s.position_x != null && s.position_y != null) {
        return { ...s, x: Math.min(92, Math.max(8, s.position_x)), y: Math.min(88, Math.max(12, s.position_y)) };
      }
      const angle = (i / arr.length) * Math.PI * 2 - Math.PI / 2;
      return { ...s, x: 50 + 38 * Math.cos(angle), y: 50 + 34 * Math.sin(angle) };
    });
  }, [shops]);

  if (!shops || shops.length === 0) return null;

  return (
    <section className="surface rounded-2xl p-4 sm:p-5" data-testid="shop-network-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display font-bold text-base md:text-lg flex items-center gap-2">
            <Store size={16} className="text-[#F59E0B]" /> Nearby Shops Network
          </h2>
          <p className="text-[10px] text-[#9CA3D4] mt-0.5">
            {gpsUsed ? "Distances calculated from your location" : "Partner shops around you"}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[9px] text-[#9CA3D4]">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B] inline-block" /> Shop</span>
          <span className="inline-flex items-center gap-1"><span className="w-4 border-t border-dashed border-[#7C3AED] inline-block" /> Distance</span>
        </div>
      </div>

      {/* Desktop / tablet — network graph */}
      <div className="hidden sm:block relative w-full" style={{ height: 340 }} data-testid="shop-network-graph">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {positioned.map((s) => (
            <line key={s.id} x1="50" y1="50" x2={s.x} y2={s.y}
                  stroke="#7C3AED" strokeOpacity="0.45" strokeWidth="0.35" strokeDasharray="1.6 1.2" />
          ))}
        </svg>
        {positioned.map((s) => (
          <div key={s.id}
               className="absolute px-1 py-0.5 rounded font-mono font-bold text-[#A78BFA] bg-[#09090F]/80 text-[9px]"
               style={{ left: `${(50 + s.x) / 2}%`, top: `${(50 + s.y) / 2}%`, transform: "translate(-50%, -50%)" }}>
            {s.distance_km != null ? `${s.distance_km} km` : ""}
          </div>
        ))}
        {/* Central hub */}
        <div className="absolute z-10" style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
          <div className="w-14 h-14 rounded-full bg-[#7C3AED] border-4 border-[#09090F] shadow-lg shadow-[#7C3AED]/40 flex flex-col items-center justify-center">
            <Navigation size={16} className="text-white" />
            <span className="text-[8px] font-bold text-white">YOU</span>
          </div>
        </div>
        {positioned.map((s) => (
          <div key={s.id} data-testid={`network-shop-${s.id}`}
               className="absolute z-10 group" style={{ left: `${s.x}%`, top: `${s.y}%`, transform: "translate(-50%, -50%)" }}>
            <div className="flex flex-col items-center w-24 text-center">
              <div className="w-11 h-11 rounded-xl bg-[#13131E] border border-[#F59E0B]/50 flex items-center justify-center
                              transition-transform group-hover:scale-110 group-hover:border-[#F59E0B] overflow-hidden">
                {s.icon_url
                  ? <img src={s.icon_url} alt="" className="w-full h-full object-cover" />
                  : <Store size={18} className="text-[#F59E0B]" />}
              </div>
              <div className="mt-1 text-[10px] font-bold text-white leading-tight truncate w-full">{s.name}</div>
              {s.location && <div className="text-[8px] text-[#9CA3D4] truncate w-full">{s.location}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile — connector spine list */}
      <div className="sm:hidden relative pl-5" data-testid="shop-network-list">
        <div className="absolute left-[9px] top-2 bottom-2 border-l border-dashed border-[#7C3AED]/50" />
        <div className="absolute left-0 top-0 w-[19px] h-[19px] rounded-full bg-[#7C3AED] flex items-center justify-center">
          <Navigation size={10} className="text-white" />
        </div>
        <div className="text-[9px] font-bold text-[#A78BFA] mb-2 ml-1">YOU</div>
        <div className="space-y-2.5">
          {shops.slice(0, 8).map((s) => (
            <div key={s.id} className="relative flex items-center gap-3 surface-2 bg-[#13131E]/60 rounded-xl p-2.5"
                 data-testid={`network-shop-m-${s.id}`}>
              <div className="absolute -left-[13px] w-2 h-2 rounded-full bg-[#F59E0B]" />
              <div className="w-9 h-9 rounded-lg bg-[#09090F] border border-[#F59E0B]/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                {s.icon_url ? <img src={s.icon_url} alt="" className="w-full h-full object-cover" /> : <Store size={15} className="text-[#F59E0B]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate">{s.name}</div>
                <div className="text-[9px] text-[#9CA3D4] flex items-center gap-1 truncate">
                  {s.location && <><MapPin size={8} /> {s.location}</>}
                </div>
                {s.description && <div className="text-[9px] text-[#9CA3D4]/80 truncate">{s.description}</div>}
              </div>
              {s.distance_km != null && (
                <div className="text-[10px] font-mono font-bold text-[#A78BFA] flex-shrink-0">{s.distance_km} km</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShopNetwork;
